import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { SubscribedContainer } from '../../../util/subscribed-container';
import { BaryComponentHelper } from '../../../util/bary-component-helper';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Transporter } from '../../../../../models/transporter.model';
import { ITransportConfiguration } from '../../../../../models/transport-configuration.model';
import { IMyDate } from 'ng-uikit-pro-standard';
import dayjs, { Dayjs } from 'dayjs';
import { TransportDateService } from '../../../../../services/transport-date-service';
import { Transport } from '../../../../../models/transport.model';
import { TransportFormGroup } from '../../../../../models/forms/TransportFormGroup';
import { TransporterEnum } from '../../../../../models/enums/transporter-enum.enum';

@Component({
  selector: 'jhi-date-picker-form',
  templateUrl: './date-picker-form.component.html',
  styleUrls: ['./date-picker-form.component.scss'],
})
export class DatePickerFormComponent extends SubscribedContainer implements OnInit {
  @Input() id = BaryComponentHelper.makeId();
  @Input() transportForm!: TransportFormGroup;
  @Input() transporter!: Transporter;
  @Input() transportConfiguration!: ITransportConfiguration;
  @Input() options: {
    isPickup: boolean;
  } = { isPickup: true };
  @Input() disabled = false;
  @Input() isUpdate?: boolean;

  constructor(private transportDateService: TransportDateService) {
    super();
  }

  switchCase<T>(pickupVal: T, deliveryVal: T): T {
    return this.options.isPickup ? pickupVal : deliveryVal;
  }

  _minDate?: Dayjs;
  allowedRange?: {
    min: number;
    max: number;
    minDefault: number;
    maxDefault: number;
    minRange: number;
  };
  datePickerOptions!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate?: IMyDate; disablePickupDays?: IMyDate[] };
  forceRefreshOptions = new EventEmitter();

  ngOnInit(): void {
    const phoneCallControl = this.switchCase(this.transportForm.controls.pickupPhoneCall, this.transportForm.controls.deliveryPhoneCall);
    const meetingControl = this.switchCase(this.transportForm.controls.pickupMeeting, this.transportForm.controls.deliveryMeeting);

    if (this.isUpdate) {
      this._minDate = dayjs('2019-01-01');
      this.forceRefreshOptions.emit(this.getDatePickerConfig());
      this.datePickerOptions = this.getDatePickerConfig();
    } else {
      this.initMinDate();
      this.refreshDateChanges();
    }

    this.allowedRange = {
      min: this.transportConfiguration.timeslotStart,
      max: this.transportConfiguration.timeslotEnd,
      minDefault: this.transportConfiguration.timeslotStartDefault,
      maxDefault: this.transportConfiguration.timeslotEndDefault,
      minRange: this.transportConfiguration.timeslotRange,
    };
    phoneCallControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(isCheck => {
      if (phoneCallControl.status === 'DISABLED') {
        return;
      } else {
        if (isCheck) {
          if (meetingControl.status !== 'DISABLED') {
            meetingControl.disable();
            meetingControl.updateValueAndValidity();
          }
        } else {
          if (meetingControl.status === 'DISABLED') {
            meetingControl.enable();
            meetingControl.updateValueAndValidity();
          }
        }
      }
    });

    meetingControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(isCheck => {
      if (meetingControl.status === 'DISABLED') {
        return;
      } else {
        if (isCheck) {
          if (phoneCallControl.status !== 'DISABLED') {
            phoneCallControl.disable();
            phoneCallControl.updateValueAndValidity();
          }
        } else {
          if (phoneCallControl.status === 'DISABLED') {
            phoneCallControl.enable();
            phoneCallControl.updateValueAndValidity();
          }
        }
      }
    });
  }

  private initMinDate(): void {
    if (this.options.isPickup) {
      const pickupZipCode = this.transportForm.controls.pickupAddress.controls.zipCode.value;
      const pickupCountry = this.transportForm.controls.pickupAddress.controls.country.value;

      this.transportDateService
        .getNextDayAvailable(dayjs(), pickupZipCode, pickupCountry, this.transportForm.controls.offerId?.value)
        .pipe(debounceTime(300))
        .subscribe(minDate => {
          this._minDate = dayjs(minDate);
          if (this._minDate.isAfter(this.transportForm.controls.pickupDate.value)) {
            this.transportForm.controls.pickupDate.setValue(this._minDate);
          }
          this.forceRefreshOptions.emit(this.getDatePickerConfig());
          this.datePickerOptions = this.getDatePickerConfig();
        });
    } else {
      const transport = new Transport({ ...this.transportForm.getAngularForm().getRawValue() });
      this.transportDateService
        .getNextDeliveryDayAvailable(transport)
        .pipe(debounceTime(300))
        .subscribe(minDate => {
          this._minDate = dayjs(minDate);
          this.transportForm.controls.deliveryDate.setValue(this._minDate);
          this.forceRefreshOptions.emit(this.getDatePickerConfig());
          this.datePickerOptions = this.getDatePickerConfig();
        });
    }
  }

  getDatePickerConfig(): { minDate: IMyDate; maxDate: IMyDate; disableUntilDate?: IMyDate; disablePickupDays?: IMyDate[] } {
    let result;
    if (this.isUpdate) {
      result = this.transportDateService.toDatePickerOptionsForUpdate(this._minDate!);
    } else if (this.options.isPickup) {
      result = this.transportDateService.toDatePickerOptions(this._minDate!);
    } else {
      result = this.transportDateService.toDatePickerOptions(this._minDate!);
    }

    // Disable single dates for Evolutrans
    if (TransporterEnum.EVOLUTRANS.technicalName == this.transporter?.technicalName) {
      const disablePickupDate = [
        { year: 2024, month: 12, day: 30 },
        { year: 2024, month: 12, day: 31 },
        { year: 2025, month: 1, day: 1 },
      ];
      result = { ...result, disablePickupDays: disablePickupDate };
    }

    return result;
  }

  private refreshDateChanges(): void {
    // Si l'adresse pickup ou delivery change, alors on refresh les dates.
    this.transportForm.controls.deliveryAddress.controls.zipCode.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.initMinDate();
    });
    this.transportForm.controls.pickupAddress.controls.zipCode.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.initMinDate();
    });
  }
}
